/** Etichette, toni e countdown per lo stato di un mercato (SPEC-010 §8). */

import type { BriefingStatus, MarketStatus } from './api';

export const STATUS_LABELS: Record<MarketStatus, string> = {
  open: 'Aperto',
  closed: 'Chiuso',
  pre_market: 'Pre-market',
  after_hours: 'After-hours',
  lunch_break: 'Pausa pranzo',
  opening_auction: 'Asta di apertura',
  closing_auction: 'Asta di chiusura',
  holiday: 'Festivo',
  weekend: 'Weekend',
};

export const STATUS_TONE: Record<MarketStatus, 'positive' | 'negative' | 'neutral' | 'accent'> = {
  open: 'positive',
  closed: 'neutral',
  pre_market: 'accent',
  after_hours: 'accent',
  lunch_break: 'neutral',
  opening_auction: 'accent',
  closing_auction: 'accent',
  holiday: 'negative',
  weekend: 'negative',
};

/** Stati in cui il mercato è dentro (o a ridosso del)la sua giornata di borsa: l'evento rilevante è la chiusura, non la prossima apertura. */
const IN_SESSION_STATUSES = new Set<MarketStatus>([
  'open',
  'pre_market',
  'after_hours',
  'lunch_break',
  'opening_auction',
  'closing_auction',
]);

/**
 * `null` per un mercato sempre aperto: non ha un prossimo evento, e mostrare
 * un countdown verso qualcosa che non arriverà sarebbe peggio che non mostrare
 * nulla. Il chiamante rende in quel caso un'etichetta statica.
 */
export function nextEventFor(item: {
  status: MarketStatus;
  nextOpen: string | null;
  nextClose: string | null;
}): { label: string; at: string } | null {
  const inSession = IN_SESSION_STATUSES.has(item.status);
  const at = inSession ? item.nextClose : item.nextOpen;
  if (!at) return null;
  return { label: inSession ? 'Chiude' : 'Apre', at };
}

/** Un mercato senza prossimo evento va in fondo all'ordinamento per vicinanza, non in testa. */
export function nextEventSortKey(item: { status: MarketStatus; nextOpen: string | null; nextClose: string | null }): number {
  const event = nextEventFor(item);
  return event ? new Date(event.at).getTime() : Number.POSITIVE_INFINITY;
}

/** "tra 42 min" / "tra 3 h 12 min" — mai un timestamp assoluto: qui conta la vicinanza, non l'orario esatto. */
export function formatCountdown(iso: string, now: Date = new Date()): string {
  const diffMs = new Date(iso).getTime() - now.getTime();
  if (diffMs <= 0) return 'a breve';
  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 60) return `tra ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const rem = minutes % 60;
  return rem === 0 ? `tra ${hours} h` : `tra ${hours} h ${rem} min`;
}

export const BRIEFING_STATUS_LABELS: Record<BriefingStatus, string> = {
  collecting: 'In raccolta',
  summarizing: 'In elaborazione',
  completed: 'Completato',
  failed: 'Non riuscito',
};

export const BRIEFING_STATUS_TONE: Record<BriefingStatus, 'positive' | 'negative' | 'neutral' | 'accent'> = {
  collecting: 'accent',
  summarizing: 'accent',
  completed: 'positive',
  failed: 'negative',
};

export const MARKET_RESOLUTION_LABELS: Record<string, string> = {
  resolved: 'Risolto automaticamente',
  manual: 'Impostato manualmente',
  ambiguous: 'Ambiguo — richiede scelta manuale',
  unresolved: 'Non risolto',
};
